import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import type { Command } from '../menu/types';
import {
  getDetailActionPresentation,
  type DetailActionPresentation,
} from '../grid/utils/commandIcons';
import { useJsMethodLoading } from '../js-methods/jsMethodLoadingContext';
import { DetailFormActionOverflow, type DetailFormActionItem } from './DetailFormActionOverflow';

type DetailFormActionsProps = {
  title?: string;
  commands?: Command[];
  isDeleted?: boolean;
  onSave: (command?: Command) => void;
  onClose?: () => void;
  onConfiguredClose?: () => void;
  onCommandClick?: (command: Command) => void;
  isSubmitting?: boolean;
  isActioning?: boolean;
  isDirty?: boolean;
  isCommandDisabled?: (command: Command) => boolean;
};

type DetailAction = DetailFormActionItem & {
  command?: Command;
  presentation: DetailActionPresentation;
};

export const DetailFormActions = ({
  title,
  commands = [],
  isDeleted = false,
  onSave,
  onClose,
  onConfiguredClose,
  onCommandClick,
  isSubmitting = false,
  isActioning = false,
  isDirty = false,
  isCommandDisabled,
}: DetailFormActionsProps) => {
  const { isAnyLoading: isJsMethodLoading } = useJsMethodLoading();
  const busy = isSubmitting || isActioning || isJsMethodLoading;
  const visibleCommands = commands.filter(
    (command) =>
      !(command.disableDeleted && isDeleted) &&
      (command.saveForm || command.closeForm || !!onCommandClick)
  );
  const hasConfiguredClose = visibleCommands.some((command) => command.closeForm);
  const actions: DetailAction[] = visibleCommands.map((command) => {
    const presentation = getDetailActionPresentation(command);
    const action: DetailAction = {
      key: command.key,
      label:
        command.caption.trim() ||
        (command.saveForm ? 'Save' : command.closeForm ? 'Close' : 'Action'),
      command,
      icon: presentation.icon,
      presentation,
    };

    return action;
  });

  if (!hasConfiguredClose && onClose) {
    actions.push({
      key: 'cancel',
      label: 'Cancel',
      presentation: { menuOnly: false, destructive: false },
    });
  }

  const handleAction = (action: DetailAction) => {
    if (!action.command) {
      onClose?.();
    } else if (action.command.saveForm) {
      onSave(action.command);
    } else if (action.command.closeForm) {
      onConfiguredClose?.();
    } else {
      onCommandClick?.(action.command);
    }
  };

  const renderAction = (action: DetailAction) => {
    if (!action.command) {
      return (
        <Button
          key={action.key}
          size="small"
          variant="outlined"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={() => handleAction(action)}
          disabled={busy}
          sx={{ flexShrink: 0 }}
        >
          {action.label}
        </Button>
      );
    }

    if (action.command.saveForm) {
      return (
        <Button
          key={action.key}
          size="small"
          variant="outlined"
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <SaveIcon fontSize="small" />}
          onClick={() => handleAction(action)}
          disabled={busy || !isDirty}
          sx={{ flexShrink: 0 }}
        >
          {isSubmitting ? 'Saving...' : action.label}
        </Button>
      );
    }

    if (action.command.closeForm) {
      return (
        <Button
          key={action.key}
          size="small"
          variant="outlined"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={() => handleAction(action)}
          disabled={busy}
          sx={{ flexShrink: 0 }}
        >
          {action.label}
        </Button>
      );
    }

    return (
      <Button
        key={action.key}
        size="small"
        variant="contained"
        color={action.presentation.destructive ? 'error' : 'primary'}
        startIcon={action.presentation.icon}
        onClick={() => handleAction(action)}
        disabled={busy || Boolean(action.command && isCommandDisabled?.(action.command))}
        sx={{ flexShrink: 0 }}
      >
        {action.label}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 0,
        minWidth: 0,
      }}
    >
      {title ? (
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ ml: 1, minWidth: 0, flexShrink: 1 }}
          noWrap
        >
          {title}
        </Typography>
      ) : null}

      <DetailFormActionOverflow
        items={actions}
        hiddenItemKeys={actions
          .filter((action) => action.presentation.menuOnly)
          .map((action) => action.key)}
        renderItem={(index) => renderAction(actions[index])}
        onSelect={(index) => handleAction(actions[index])}
        isItemDisabled={(index) => {
          const action = actions[index];
          return (
            busy ||
            (action.command?.saveForm && !isDirty) ||
            Boolean(action.command && isCommandDisabled?.(action.command))
          );
        }}
        isItemDestructive={(index) => actions[index].presentation.destructive}
      />
    </Box>
  );
};
